import React from "react";
import { isAxiosError } from "axios";
import {
  WhmDialog,
  WhmDialogTitle,
  WhmDialogContent,
  WhmDialogActions,
  WhmIconButton,
  WhmBox,
  WhmTypography,
  WhmButton,
  WhmCircularProgress,
  useMediaQuery,
} from "@securitize/reactjs-whm";
import type { Theme } from "@securitize/reactjs-whm";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import dayjs from "dayjs";
import type {
  AssetWithPosition,
  ReportSection,
  DateRange,
} from "../../../data/types/portfolio-statement/portfolio-statement";
import { PortfolioStatementClient } from "../../../data/clients/portfolio-statement.client";
import { useAssetSelection } from "../../../hooks/useAssetSelection";
import { useAssetOrdering } from "../../../hooks/useAssetOrdering";
import { useSnackbarContext } from "../../shared/Snackbar";
import type { TranslationKey } from "../../../contexts/I18nContext/translationKeys";
import { translationKeys, useTranslation } from "../../../hooks/useTranslation";
import { Translate } from "../../shared/translation";
import { downloadFile } from "../../../utils/downloadUtils";
import { PulsatingSpinner } from "../../shared/PulsatingSpinner";
import { DateRangeSection } from "./DateRangeSection";
import { IncludedSectionsPanel } from "./IncludedSectionsPanel";
import { AssetSelectionList } from "./AssetSelectionList";

interface PortfolioStatementModalProps {
  open: boolean;
  onClose: () => void;
}

/** Default inception date for the starting date. */
const DEFAULT_START_DATE = dayjs("2024-01-03");

/**
 * Returns the appropriate translation key for an error based on HTTP status code.
 */
const getErrorTranslationKey = (error: unknown): TranslationKey => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    switch (status) {
      case 401:
        return translationKeys.PORTFOLIO_STATEMENT_UNAUTHORIZED;
      case 403:
        return translationKeys.PORTFOLIO_STATEMENT_FORBIDDEN;
      case 422:
        return translationKeys.PORTFOLIO_STATEMENT_INVALID_REQUEST;
      case 429:
        return translationKeys.PORTFOLIO_STATEMENT_RATE_LIMITED;
      case 500:
        return translationKeys.PORTFOLIO_STATEMENT_SERVER_ERROR;
      default:
        return translationKeys.PORTFOLIO_STATEMENT_DOWNLOAD_ERROR;
    }
  }

  if (
    error instanceof Error &&
    error.message === "Statement generation timed out"
  ) {
    return translationKeys.PORTFOLIO_STATEMENT_TIMEOUT;
  }

  return translationKeys.PORTFOLIO_STATEMENT_DOWNLOAD_ERROR;
};

export const PortfolioStatementModal: React.FC<
  PortfolioStatementModalProps
> = ({ open, onClose }) => {
  const isDesktopModal = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("xl"),
  );
  const { getTranslation: t } = useTranslation();
  const snack = useSnackbarContext();

  // Data state
  const [assets, setAssets] = React.useState<AssetWithPosition[]>([]);
  const [sections, setSections] = React.useState<ReportSection[]>([]);
  const [loadingAssets, setLoadingAssets] = React.useState(true);
  const [assetsError, setAssetsError] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);

  // AbortController ref for cancelling in-flight requests
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // Date range state
  const [dateRange, setDateRange] = React.useState<DateRange>({
    startDate: DEFAULT_START_DATE,
    endDate: dayjs(),
  });

  // Section selection state
  const [selectedSectionIds, setSelectedSectionIds] = React.useState<
    Set<string>
  >(new Set());

  const handleToggleSection = React.useCallback((sectionId: string) => {
    setSelectedSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Token/asset selection state
  const availableAssets = React.useMemo(
    () => assets.filter((a) => a.hasStatementAvailable),
    [assets],
  );

  const availableAssetIds = React.useMemo(
    () => availableAssets.map((a) => a.id),
    [availableAssets],
  );

  const { selectedAssetIds, toggleAsset, isSelected, selectedCount } =
    useAssetSelection(availableAssetIds);

  // Frontend ordering as fallback - send assetOrder in request
  const { orderedAssets } = useAssetOrdering(availableAssets);

  // Fetch assets and sections when modal opens
  const fetchData = React.useCallback(async () => {
    // Abort any previous in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoadingAssets(true);
      setAssetsError(false);

      const [assetsData, sectionsData] = await Promise.all([
        PortfolioStatementClient.getInvestorAssets(controller.signal),
        PortfolioStatementClient.getReportSections(),
      ]);

      setAssets(assetsData);
      setSections(sectionsData);

      // Initialize section selection with defaults
      const defaultSections = new Set(
        sectionsData.filter((s) => s.defaultChecked).map((s) => s.id),
      );
      setSelectedSectionIds(defaultSections);
    } catch (error) {
      // Ignore abort errors
      if (error instanceof DOMException && error.name === "AbortError") return;
      setAssetsError(true);
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) {
      fetchData();
      // Reset date range when modal opens
      setDateRange({
        startDate: DEFAULT_START_DATE,
        endDate: dayjs(),
      });
    }

    return () => {
      // Abort any in-flight requests when modal closes
      abortControllerRef.current?.abort();
    };
  }, [open, fetchData]);

  const handleDownloadPdf = async () => {
    // Abort any previous in-flight generation request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setGenerating(true);

      // Build asset order from the frontend ordering
      const assetOrder = orderedAssets
        .filter((a) => selectedAssetIds.includes(a.id))
        .map((a) => a.id);

      const result = await PortfolioStatementClient.generateStatement(
        {
          assetIds: selectedAssetIds,
          dateRange: {
            startDate: dateRange.startDate?.format("YYYY-MM-DD") || "",
            endDate: dateRange.endDate?.format("YYYY-MM-DD") || "",
          },
          sections: Array.from(selectedSectionIds),
          assetOrder,
        },
        controller.signal,
      );

      if (result.url) {
        // Check if the URL has expired
        if (result.expiresAt && dayjs(result.expiresAt).isBefore(dayjs())) {
          throw new Error("Statement generation timed out");
        }

        downloadFile(result.url);

        snack.showSnackbar({
          message: (
            <WhmBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckIcon fontSize="small" />
              {t(translationKeys.PORTFOLIO_STATEMENT_DOWNLOAD_SUCCESS)}
            </WhmBox>
          ),
          severity: "info",
        });
        onClose();
      }
    } catch (error) {
      // Ignore abort errors (user closed the modal)
      if (error instanceof DOMException && error.name === "AbortError") return;

      const errorKey = getErrorTranslationKey(error);

      snack.showSnackbar({
        message: (
          <WhmBox
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
            }}
          >
            <WhmTypography variant="body2" sx={{ color: "inherit" }}>
              {t(errorKey)}
            </WhmTypography>
            <WhmButton
              variant="outlined"
              fullWidth
              onClick={() => {
                snack.hideSnackbar();
                handleDownloadPdf();
              }}
              sx={{
                color: "inherit",
                borderColor: "inherit",
                "&:hover": { borderColor: "inherit", opacity: 0.8 },
              }}
            >
              <Translate
                translationKey={translationKeys.PORTFOLIO_STATEMENT_TRY_AGAIN}
              />
            </WhmButton>
          </WhmBox>
        ),
        severity: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  const isDownloadDisabled =
    selectedCount === 0 ||
    selectedSectionIds.size === 0 ||
    loadingAssets ||
    !dateRange.startDate ||
    !dateRange.endDate;

  const renderContent = () => {
    if (loadingAssets) {
      return (
        <WhmBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <PulsatingSpinner />
        </WhmBox>
      );
    }

    if (assetsError) {
      return (
        <WhmBox sx={{ textAlign: "center", py: 4 }}>
          <WhmTypography variant="body1" color="text.secondary">
            <Translate
              translationKey={translationKeys.PORTFOLIO_STATEMENT_ASSETS_ERROR}
            />
          </WhmTypography>
        </WhmBox>
      );
    }

    if (availableAssets.length === 0) {
      return (
        <WhmBox sx={{ textAlign: "center", py: 4 }}>
          <WhmTypography variant="body1" color="text.secondary">
            <Translate
              translationKey={translationKeys.PORTFOLIO_STATEMENT_NO_ASSETS}
            />
          </WhmTypography>
        </WhmBox>
      );
    }

    return (
      <>
        {/* Section 1: Date Range */}
        <DateRangeSection
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          tokenName={
            availableAssets.length === 1
              ? availableAssets[0].tokenName
              : undefined
          }
          isDesktop={isDesktopModal}
        />

        {/* Section 2: Included Sections */}
        <IncludedSectionsPanel
          sections={sections}
          selectedSectionIds={selectedSectionIds}
          onToggleSection={handleToggleSection}
        />

        {/* Section 3: Included Tokens */}
        <AssetSelectionList
          assets={availableAssets}
          isSelected={isSelected}
          toggleAsset={toggleAsset}
        />
      </>
    );
  };

  return (
    <WhmDialog
      open={open}
      onClose={onClose}
      aria-labelledby="portfolio-statement-dialog-title"
      fullWidth
      maxWidth={isDesktopModal ? "sm" : false}
      fullScreen={!isDesktopModal}
    >
      <WhmBox
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <WhmDialogTitle
          id="portfolio-statement-dialog-title"
          variant="h4"
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Translate
            translationKey={translationKeys.PORTFOLIO_STATEMENT_MODAL_TITLE}
          />
          <WhmIconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={(theme: Theme) => ({
              padding: 0,
              paddingTop: "7px",
              alignSelf: "self-start",
              "&:hover": {
                backgroundColor: "#fff",
              },
              color: theme.palette.common.black,
            })}
          >
            <CloseIcon />
          </WhmIconButton>
        </WhmDialogTitle>

        <WhmDialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            borderBottom: "none",
          }}
        >
          {renderContent()}
        </WhmDialogContent>

        {!loadingAssets && !assetsError && availableAssets.length > 0 && (
          <WhmDialogActions
            sx={{
              "&.MuiDialogActions-root": { borderTop: "none" },
              padding: !isDesktopModal ? "16px" : "16px 24px",
              position: !isDesktopModal ? "sticky" : "static",
              bottom: !isDesktopModal ? 0 : "auto",
              backgroundColor: "background.paper",
            }}
          >
            <WhmButton
              color="primary"
              variant="contained"
              onClick={handleDownloadPdf}
              disabled={isDownloadDisabled || generating}
              fullWidth={!isDesktopModal}
              sx={{
                alignSelf: !isDesktopModal ? "stretch" : "flex-end",
                position: "relative",
              }}
            >
              <WhmBox
                component="span"
                sx={{ visibility: generating ? "hidden" : "visible" }}
              >
                <Translate
                  translationKey={
                    translationKeys.PORTFOLIO_STATEMENT_DOWNLOAD_PDF
                  }
                />
              </WhmBox>
              {generating && (
                <WhmCircularProgress
                  size={24}
                  color="inherit"
                  sx={{ position: "absolute" }}
                />
              )}
            </WhmButton>
          </WhmDialogActions>
        )}
      </WhmBox>
    </WhmDialog>
  );
};
